import { FC, useMemo, useCallback, memo } from 'react'
import { CategoryId } from 'interfaces/meta'
import { CategorizedPage } from 'interfaces/pages'
import { CategoriesMenu } from 'components/features/pfm/CategoriesMenu/CategoriesMenu'
import InlineDropdownWrapper from 'components/atoms/dropdowns/InlineDropdownWrapper'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { setFilterCategoryActionCreator } from 'actions/globalActions'
import { trackUIEventAction } from 'actions/analyticsActions'
import { metaSelector } from 'selectors/globalSelectors'
import { saturateCategory } from 'helpers/categoriesHelpers'
import { TRANSACTIONS_PAGE } from 'constants/pages'
import { ALL_CATEGORY_ID, ALL_ACCOUNT_CATEGORY_TITLE } from 'constants/meta'
import { isAccountPage } from 'helpers/pageHelpers'
import { ALL_MONEYFLOW_CATEGORY } from 'helpers/rawHelpers'
import { useSelectedCategoryId } from 'helpers/hookHelpers'

export type CategorySelectProps = {
  type: CategorizedPage
  withArrow?: boolean
  className?: string
  captionClassName?: string
}

export const CategorySelect: FC<CategorySelectProps> = memo((props) => {
  const {
    type,
    className,
    captionClassName,
    withArrow = true,
  } = props

  const dispatch = useStoreDispatch()
  const selectedCategoryId = useSelectedCategoryId(props.type)
  const meta = useStoreSelector(metaSelector)

  const handleSelectCategory = useCallback<(selectedId: CategoryId) => void>((id) => {
    dispatch(setFilterCategoryActionCreator(type, id))

    dispatch(trackUIEventAction({
      category: 'site_controls-category_filter',
      action: 'select',
      label: `${ selectedCategoryId } → ${ id }`,
    }))
  }, [dispatch, selectedCategoryId, type])

  const selectedCaption = useMemo(() => {
    return saturateCategory(selectedCategoryId, meta)
  }, [meta, selectedCategoryId])

  const handleGetCaption = useCallback(() => {
    const categoryId = selectedCaption ? selectedCaption.id : null
    if (categoryId === ALL_CATEGORY_ID) {
      return isAccountPage(type) ? ALL_ACCOUNT_CATEGORY_TITLE : ALL_MONEYFLOW_CATEGORY.value
    } else if (selectedCaption) {
      return selectedCaption.value
    } else {
      return ''
    }
  }, [selectedCaption, type])

  return (
    <InlineDropdownWrapper
      selected={ selectedCategoryId as any }
      getCaption={ handleGetCaption }
      qaMarker='category-dropdown'
      className={ className }
      captionClassName={ captionClassName }
      withArrow={ withArrow }
    >
      <CategoriesMenu
        selectedId={ selectedCategoryId }
        onSelect={ handleSelectCategory }
        type={ type }
        allowAllCategories
        allowGroups={ type === TRANSACTIONS_PAGE }
      />
    </InlineDropdownWrapper>
  )
})
